import Splide, { type Options } from '@splidejs/splide';
import { Build, Component, Element, Host, Prop, Watch, h } from '@stencil/core';

import {
  getTeaserText,
  getTenant,
  isEnterKey,
  openUrl,
  renderResponsiveImages,
  renderTeaser as renderStandardTeaser,
  renderStandardTeaserErrorMassages,
} from '../../helpers/helpers';
import type {
  ContentServiceResult,
  CssClassMap,
  FetchTeaserServiceConfig,
  ResponsiveImages,
  SplideComponentConfig,
  Teaser,
} from '../../helpers/utils/interfaces';

import { getSvg } from '../../assets/svg/svg';
import { mapContentServiceResultToTeaserList } from '../../helpers/contentServiceComponent';
import {
  ContentTeaserData,
  defaultFetchConfig,
} from '../../helpers/services/ContentTeaserData';
import { updateNextArrowState } from '../../helpers/splide-helper';

@Component({
  tag: 'cms-teaser-rondel',
  styleUrl: 'cms-teaser-rondel.scss',
  shadow: true,
})
export class CmsTeaserRondel {
  private teaserList: Teaser[];

  private readonly loadDataConfig = {
    ...defaultFetchConfig,
    ...{
      locale: null,
      companyCode: null,
      responsePage: 1,
      responseTeaserCount: 9,
      tag: null,
    },
  } as FetchTeaserServiceConfig;

  private readonly splideConfig = {
    type: 'slide',
    perMove: 1,
    gap: '0.75rem',
    autoWidth: true,
    rewind: false,
    slideFocus: false,
    pagination: false,
    trimSpace: true,
    autoHeight: true,
    focusableNodes: 'li',
    role: 'carousel',
  } as Options;

  private teaserResult: ContentServiceResult | undefined;
  private splideNode: HTMLElement;
  private splideObj: Splide;
  private splideTranslations: SplideComponentConfig;

  private readonly imageSizes = {
    '1': `(max-width: 1279px) 55vw,
    (min-width: 1280px) 752px,
    100vw`,
    '2': `(max-width: 767px) 55vw,
    (min-width: 768px) and (max-width: 1279px) 33vw,
    (min-width: 1280px) 451px,
    100vw`,
    '3': `(max-width: 767px) 55vw,
    (min-width: 768px) and (max-width: 1279px) 25vw,
    (min-width: 1280px) 320px,
    100vw`,
  };

  private readonly displaySlidesObj = {
    '1': 'cut-single-slide',
    '2': 'cut-double-slide',
    '3': 'cut-triple-slide',
  };

  /**
   * set the user locale like de_DE
   */
  @Prop({ mutable: true }) locale: string | undefined;
  /**
   * set the user companyCode like 1001
   */
  @Prop({ mutable: true }) companyCode: string | undefined;
  /**
   * Set the Filter Type like 'rubric', 'tag' or 'teaser' to use in the query for request the content service
   */
  @Prop({ mutable: true }) filterType: string;
  /**
   * Set the filter uuid represent like bauen, wohnen etc.  to use in the query for request the content service
   */
  @Prop({ mutable: true }) filterId?: string;
  /**
   * Set the teaser list in case of filterType 'teaser'
   */
  @Prop() teaserData?: Teaser[] | string;
  /**
   * Set the download pdf label
   */
  @Prop() downloadPdfLabel: string;
  /**
   * is true only in cms preview
   */
  @Prop() isPreview: false;
  /**
   * Set the Component Headline to use in cms-headline
   */
  @Prop({ mutable: true }) headline: string;
  /**
   * Set the count of Slides to display
   */
  @Prop({ mutable: true }) displaySlides: '1' | '2' | '3';

  /**
   * Set the more teaser label like ...mehr
   */
  @Prop({ mutable: true }) moreLabelText: string;

  /**
   * Set the tracking namespace (optional)
   */
  @Prop({ mutable: true }) namespace?: string;

  /**
   * Set the translations for splide
   */
  @Prop() translations: SplideComponentConfig | string;

  @Element() hostElement: HTMLElement;

  @Watch('teaserData')
  private teaserWatcher(newValue: string | Teaser[]) {
    if (newValue) {
      this.teaserList =
        typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
    }
  }

  @Watch('translations')
  private translationsWatcher(newValue: SplideComponentConfig | string) {
    if (newValue) {
      this.splideTranslations =
        typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
    }
  }

  private mountSplide() {
    if (!Build.isBrowser || this.teaserResult === undefined) {
      return;
    }
    this.splideObj = new Splide(this.splideNode, this.splideConfig);
    this.splideObj.on('ready resized visible moved', () =>
      updateNextArrowState(this.splideObj),
    );

    this.splideObj.mount();
  }

  private renderImages(imageDerivates, altText: string) {
    if (imageDerivates) {
      const imagesObj: ResponsiveImages = {
        bp320: `${imageDerivates['320w']}.webp`,
        bp480: `${imageDerivates['480w']}.webp`,
        bp768: `${imageDerivates['768w']}.webp`,
        bp992: `${imageDerivates['992w']}.webp`,
        bp1200: `${imageDerivates['1200w']}.webp`,
        jpg992Default: imageDerivates['992w'],
      };

      return renderResponsiveImages(
        imagesObj,
        this.imageSizes[this.displaySlides],
        altText,
        'teaser-rondel-image',
      );
    }
  }

  private splideSlideCssMap(): CssClassMap {
    return {
      splide__slide: true,
      [this.displaySlidesObj[this.displaySlides]]: true,
    };
  }

  private splideListCssMap(): CssClassMap {
    return {
      splide__list: true,
      [this.displaySlidesObj[this.displaySlides]]: true,
    };
  }

  private renderTeaser() {
    const bgImageStyles = {
      minHeight: '3px',
      height: '5px',
      display: 'flex',
      position: 'absolute',
      top: '-6px',
      left: '0',
      zIndex: '100',
      width: '100%',
    };

    const contentWrapperStyles = {
      position: 'relative',
      backgroundColor: 'var(--cms-semanticColors-base-medium-background)',
    };

    const teasers = mapContentServiceResultToTeaserList(this.teaserResult);
    if (!teasers) {
      return;
    }

    if (teasers.length > 0) {
      return teasers.map((item: Teaser) => {
        const {
          uid,
          headline,
          detail,
          imageDerivates,
          url,
          altText,
          badge,
        }: Teaser = item;
        return (
          <li
            onClick={() => openUrl(url)}
            onKeyDown={(event) => {
              if (isEnterKey(event)) {
                openUrl(url);
              }
            }}
            key={uid}
            class={this.splideSlideCssMap()}
            {...{
              ariaHidden: false,
              tabIndex: 0,
            }}
          >
            <div class={'splide__slide__container'}>
              <div class={'badge-wrapper'}>
                {badge != null && badge !== '' ? (
                  <cms-badge name={badge} theme='service' size='badge-sm' />
                ) : null}
              </div>
              <div class={'teaser-rondel-link'}>
                {this.renderImages(imageDerivates, altText)}
                <div class='content-wrapper'>
                  <div
                    class='content-wrapper-inner'
                    style={contentWrapperStyles}
                  >
                    {getTenant() === 'hornbach' ? (
                      <div
                        innerHTML={getSvg('cardScratch')}
                        style={bgImageStyles}
                        class={'scratch'}
                      />
                    ) : null}
                    {headline !== undefined ? (
                      <cms-headline
                        style={{
                          '--cms-theme-text-color':
                            'var(--cms-semanticColors-base-strong-text)',
                        }}
                        headlineText={headline}
                        headlineTag='h3'
                        headlineClass={'teaser-rondel-headline'}
                      />
                    ) : null}
                    <div
                      class={'bg-color teaser-rondel-text'}
                      innerHTML={
                        detail !== undefined
                          ? getTeaserText(detail, this.moreLabelText)
                          : null
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </li>
        );
      });
    }
  }

  connectedCallback() {
    if (!this.teaserData) return;
    this.teaserWatcher(this.teaserData);
  }

  async componentWillLoad(): Promise<void> {
    if (this.translations) {
      this.translationsWatcher(this.translations);
    }

    if (this.locale === undefined || this.companyCode === undefined) {
      this.teaserResult = undefined;
      return;
    }

    // Only load teasers of pages with videos or images - no youtube-videos
    Object.assign(this.loadDataConfig, {
      [this.filterType]: this.filterId,
      locale: this.locale,
      companyCode: this.companyCode,
      mediaType: 'IMAGE;VIDEO',
    });

    this.teaserResult = await ContentTeaserData.getDataFromService(
      this.loadDataConfig,
    );
  }

  componentDidLoad() {
    this.splideNode = this.hostElement.shadowRoot.querySelector('.splide');
    if (this.splideNode !== undefined) {
      this.mountSplide();
    }
  }

  render() {
    return (
      <Host class={'teaser-rondel'}>
        <div class={'teaser-rondel-inner'}>
          <div class={'splide'}>
            {this.headline !== undefined ? (
              <cms-headline
                headlineText={this.headline}
                headlineTag={'h2'}
                headlineClass={'subheading'}
                headlinePosition={'center'}
                isAdaptive={true}
              />
            ) : null}
            <div class={'splide-inner'}>
              <div class='splide__arrows'>
                <cms-button
                  namespace={`${this.namespace}-arrow-left`}
                  class={'splide-arrow splide__arrow--prev'}
                  theme='secondary'
                  size='button-sm'
                  type='button'
                  icon='arrowLeft'
                  icon-size='sm'
                  tag-title={this.splideTranslations?.[0]?.arrowLeftLabel ?? ''}
                  focusable={false}
                />
                <cms-button
                  namespace={
                    this.namespace ? `${this.namespace}-arrow-right` : ''
                  }
                  class={'splide-arrow splide__arrow--next'}
                  theme='secondary'
                  size='button-sm'
                  type='button'
                  icon='arrowRight'
                  icon-size='sm'
                  tag-title={
                    this.splideTranslations?.[0]?.arrowRightLabel ?? ''
                  }
                  focusable={false}
                />
              </div>
              <div class={'splide__track dynamic-teasers'}>
                <ul class={this.splideListCssMap()}>
                  {this.teaserResult !== undefined
                    ? this.chooseRenderBasedOnTeaserType()
                    : null}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }

  private chooseRenderBasedOnTeaserType() {
    return this.filterType === 'teaser'
      ? this.renderStandardTeaserList(this.teaserList)
      : this.renderTeaser();
  }

  private renderStandardTeaserList(teaserList: Teaser[]) {
    if (teaserList.length === 0) {
      return;
    }

    return teaserList?.map((teaser: Teaser) => {
      if (this.isPreview && teaser.previewErrors) {
        return renderStandardTeaserErrorMassages(teaser.previewErrors);
      }

      const { uid }: Teaser = teaser;

      if (!teaser.previewErrors) {
        return (
          <li key={uid} class={this.splideSlideCssMap()}>
            {renderStandardTeaser(
              teaser,
              this.imageSizes.toString(),
              this.moreLabelText,
              null,
              this.downloadPdfLabel ?? '',
            )}
          </li>
        );
      }
    });
  }
}
